import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import useAdminTimeout from '../../hooks/useAdminTimeout';
import AdminTimeoutModal from '../../components/AdminTimeoutModal';
import {
  GraduationCap, LayoutDashboard, BookOpen, MessageSquare,
  LogOut, Menu, X, ExternalLink, ChevronRight, Megaphone, Tag, Sparkles
} from 'lucide-react';

const NAV = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { label: 'Courses', href: '/admin/courses', icon: BookOpen },
  { label: 'Enquiries', href: '/admin/enquiries', icon: MessageSquare },
  { label: 'Sale Banner', href: '/admin/banner', icon: Megaphone },
  { label: 'Popup Ad', href: '/admin/popup-ad', icon: Sparkles },
  { label: 'Promo Codes', href: '/admin/promo-codes', icon: Tag },
];

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/admin'); };

  const { showWarning, countdown, resetTimer } = useAdminTimeout({ onExpire: handleLogout });
  const isActive = (href) => location.pathname === href;

  const Sidebar = ({ mobile = false }) => (
    <aside className={`${
      mobile
        ? 'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ' + (sidebarOpen ? 'translate-x-0' : '-translate-x-full')
        : 'hidden lg:flex w-60 flex-col fixed inset-y-0 left-0 z-40'
    } bg-gray-950 border-r border-gray-800 flex flex-col`}>
      {/* Logo */}
      <div className="p-5 border-b border-gray-800">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <GraduationCap size={17} className="text-white" />
          </div>
          <div>
            <p className="text-white font-extrabold text-xs leading-none">Atlas</p>
            <p className="text-blue-400 font-semibold text-[10px] tracking-widest">ACADEMY</p>
          </div>
        </Link>
        <div className="mt-2 pt-2 border-t border-gray-800/50">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider">Admin Panel</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map(({ label, href, icon: Icon }) => (
          <Link
            key={href}
            to={href}
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive(href)
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <Icon size={17} />
            {label}
            {isActive(href) && <ChevronRight size={13} className="ml-auto" />}
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-800 space-y-2">
        <Link to="/" target="_blank"
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors px-3 py-2 rounded-lg hover:bg-gray-800">
          <ExternalLink size={13} /> View Public Site
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 text-xs text-gray-500 hover:text-red-400 transition-colors px-3 py-2 rounded-lg hover:bg-gray-800">
          <LogOut size={13} /> Sign Out ({admin?.username})
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile sidebar */}
      <Sidebar mobile />
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center gap-3 sticky top-0 z-30">
          <button
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="flex-1">
            <h2 className="font-semibold text-gray-900 text-sm">
              {NAV.find(n => n.href === location.pathname)?.label || 'Admin'}
            </h2>
          </div>
          <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
            {admin?.username}
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>

      {showWarning && (
        <AdminTimeoutModal
          countdown={countdown}
          onStay={resetTimer}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}
